import type { StudyMissionSelectDto } from '@@types/api.types';
import { useGetMeiaStudyMissionMeasures } from '@queries/meia.query';
import { ArrowRightIcon } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import { getMeiaMissionTitle } from './meia-mission-display.util';
import { MeiaPhaseAdvanceBar } from './meia-phase-advance-bar.component';
import { getMeiaMissionPhaseProgress, getMeiaStudyPhaseProgress } from './meia-phase-progress.util';

type MeiaP1AdvanceBarProps = {
  missions: StudyMissionSelectDto[];
  studyId: string;
};

const MISSION_BREAKDOWN_TITLE_LENGTH = 22;

export const MeiaP1AdvanceBar: FC<MeiaP1AdvanceBarProps> = (props) => {
  const { missions, studyId } = props;
  const [searchParams] = useSearchParams();
  const { measureFlagsByMissionId } = useGetMeiaStudyMissionMeasures({ missions });

  const requestedMissionId = searchParams.get('missionId');
  const activeMissionId = missions.some((mission) => mission.id === requestedMissionId)
    ? requestedMissionId
    : undefined;

  const progressInputs = useMemo(
    () => ({
      isStudySignedOff: false,
      measureFlagsByMissionId,
      stages: [],
      taskRows: [],
      threadScoresByThreadId: new Map<string, number>(),
      threads: [],
    }),
    [measureFlagsByMissionId],
  );

  const studyProgress = useMemo(
    () => getMeiaStudyPhaseProgress('p1', { ...progressInputs, missions }),
    [missions, progressInputs],
  );

  const missionBreakdown = useMemo(
    () =>
      missions.map((mission) => ({
        missionId: mission.id,
        progress: getMeiaMissionPhaseProgress('p1', { ...progressInputs, mission }),
        title: getMeiaMissionTitle(mission).slice(0, MISSION_BREAKDOWN_TITLE_LENGTH),
      })),
    [missions, progressInputs],
  );

  return (
    <MeiaPhaseAdvanceBar
      caption={
        missions.length > 1 ? (
          <span>
            {missionBreakdown.map((entry, index) => (
              <span key={entry.missionId}>
                {entry.title}: {entry.progress}%{index < missionBreakdown.length - 1 && <span> · </span>}
              </span>
            ))}
            <span> · G1 is soft (auto-advance enabled)</span>
          </span>
        ) : (
          <span>G1 is soft (auto-advance enabled)</span>
        )
      }
      forwardLink={{
        children: (
          <>
            Advance to Phase 2 <ArrowRightIcon className="size-4" />
          </>
        ),
        to: meiaRoute.studyTab({
          query: activeMissionId ? { missionId: activeMissionId } : undefined,
          studyId,
          tab: 'p2',
        }),
      }}
      title={
        <>
          Phase 1 Progress: {studyProgress}%
          {missions.length > 1 && (
            <span className="font-normal text-gray-400"> · total across {missions.length} missions</span>
          )}
        </>
      }
    />
  );
};
